/**
 * General Terms and Conditions of Purchase, transcribed verbatim from the
 * executed document dated 31 July 2026.
 *
 * The wording is reviewed legal text and must not be edited, reordered or
 * paraphrased for style. It lives here as data rather than as markup so that a
 * revision from counsel can be dropped in as text, and so the page's layout can
 * change without anyone touching a clause. When the document is revised, replace
 * these sections and update EFFECTIVE_DATE together.
 */

export const TERMS_EFFECTIVE_DATE = "July 31, 2026";

/** A paragraph, or a numbered list of the kind the document uses throughout. */
export type TermsBlock = { p: string } | { ol: string[] };

export type TermsSection = { title: string; blocks: TermsBlock[] };

/** The unnumbered text between the title and clause 1. */
export const TERMS_PREAMBLE: string[] = [
  "In these General Terms and Conditions of Purchase, “Buyer” shall mean Gaias, Inc., doing business as Kono.ai, together with any of its Affiliates identified in an applicable Order.",
  "“Kono.ai” or the “Platform” means the Kono.ai website, applications, interfaces, portals, communications systems, and related procurement and marketplace services operated by or on behalf of Gaias, Inc.",
];

export const TERMS_SECTIONS: TermsSection[] = [
  {
    title: "1. Agreement",
    blocks: [
      {
        p: "These General Terms and Conditions of Purchase (“Terms and Conditions”) shall govern and form an integral part of all agreements entered into and all purchase orders, electronic orders, accepted quotations, statements of work, releases, or other procurement documents issued or accepted by Buyer for the supply of goods or services by a supplier, seller, vendor, manufacturer, distributor, service provider, or other contracting party (“Supplier”).",
      },
      {
        p: "Each such purchase order, electronic order, accepted quotation, statement of work, release, or other agreement shall be referred to in these Terms and Conditions as an “Order.” These Terms and Conditions, together with the applicable Order and any specifications, drawings, certifications, schedules, or other documents expressly incorporated into the Order, shall collectively constitute the “Agreement.”",
      },
      {
        p: "As used herein, the term “goods” includes tangible and intangible goods, including raw materials, metals, alloys, mill products, components, parts, surplus materials, excess inventory, remnants, software, documentation, technical data, certificates, reports, testing records, packaging, spare parts, and any related services. References to goods shall, where appropriate, be deemed to include services.",
      },
      {
        p: "“Documentation” includes, as applicable, material test reports, mill test reports, certificates of conformance, inspection reports, test results, heat and lot information, traceability records, country-of-origin information, country-of-melt information, safety data sheets, export classifications, compliance records, and any other documentation required by the Agreement.",
      },
      {
        p: "The term “Affiliate” of Buyer or Supplier means any entity or person that: (i) is Controlled by Buyer or Supplier; (ii) Controls Buyer or Supplier; or (iii) is under common Control with Buyer or Supplier. For this purpose, “Control” means that more than fifty percent (50%) of the controlled entity’s shares, voting rights, or ownership interests representing the right to make decisions for such entity are owned or controlled, directly or indirectly, by the controlling entity. An entity shall be considered an Affiliate only for so long as such ownership or control exists.",
      },
      {
        p: "These Terms and Conditions constitute all terms and conditions applicable to an Agreement between Buyer and Supplier relating to the purchase by Buyer and sale by Supplier of goods, unless Buyer expressly agrees otherwise in a written agreement signed by an authorized representative of Buyer.",
      },
      {
        p: "Any terms and conditions contained in a Supplier quotation, acknowledgment, invoice, website, portal, shipping document, confirmation, or other document issued by Supplier, whether before or after the issuance of an Order, are expressly rejected and shall not apply to the Agreement unless Buyer specifically accepts those terms in a written document signed by an authorized representative of Buyer.",
      },
      {
        p: "No Order or payment by Buyer constitutes acceptance of Supplier’s terms and conditions. Any amendment to these Terms and Conditions or an Agreement must be expressly agreed to in writing by an authorized representative of Buyer.",
      },
      {
        p: "In the event of a conflict among the documents forming the Agreement, the following order of precedence shall apply unless otherwise expressly stated in the Order:",
      },
      {
        ol: [
          "A separately negotiated written agreement signed by Buyer and Supplier;",
          "The applicable Order and any special terms stated in the Order;",
          "These Terms and Conditions;",
          "Specifications, drawings, and other documents incorporated into the Order; and",
          "Supplier’s quotation, solely with respect to the description, quantity, price, and delivery information expressly accepted by Buyer.",
        ],
      },
      {
        p: "Any Order issued by Buyer is expressly conditioned upon Supplier’s assent to all terms contained in the Order and these Terms and Conditions without deviation. Supplier’s acceptance may be evidenced by:",
      },
      {
        ol: [
          "Supplier’s written, verbal, or electronic acceptance;",
          "Supplier’s submission or confirmation of a quotation through the Platform;",
          "Supplier’s acknowledgment of an Order;",
          "Supplier’s commencement of work;",
          "Supplier’s shipment or delivery of goods;",
          "Supplier’s submission of an invoice; or",
          "Any other conduct by Supplier or its representatives consistent with acceptance of the Order.",
        ],
      },
      {
        p: "Electronic records, communications, approvals, acknowledgments, and acceptances made through the Platform shall have the same force and effect as written and signed records.",
      },
    ],
  },
  {
    title: "2. Timing, Charges, and Information",
    blocks: [
      {
        p: "Time is of the essence with respect to Supplier’s obligations under the Agreement.",
      },
      {
        p: "If Supplier anticipates any difficulty in meeting an agreed delivery date, quantity, specification, certification requirement, documentation requirement, or other obligation under the Agreement, Supplier shall immediately notify Buyer in writing and through the Platform, when applicable. Such notice shall include:",
      },
      {
        ol: [
          "The reason for the anticipated delay or noncompliance;",
          "The goods or services affected;",
          "The expected duration of the delay;",
          "The corrective action Supplier proposes to take; and",
          "The earliest date on which Supplier can fully perform its obligations.",
        ],
      },
      {
        p: "Supplier shall use commercially reasonable best efforts to accommodate Buyer’s requests to reschedule confirmed delivery dates, modify quantities, change delivery locations, expedite performance, or otherwise modify an Order.",
      },
      {
        p: "Upon Buyer’s request, Supplier shall promptly provide written information concerning the status of any Order, including production status, inventory availability, testing status, shipment status, expected delivery dates, outstanding Documentation, invoices, payments, and any other information reasonably requested by Buyer.",
      },
      {
        p: "Supplier shall immediately inform Buyer of any actual or anticipated constraint on Supplier’s ability to supply goods as ordered. Upon request, Supplier shall provide assurances reasonably satisfactory to Buyer that Buyer’s requirements will be adequately fulfilled.",
      },
      {
        p: "Supplier shall not impose any additional charge, surcharge, expedite fee, handling fee, packaging fee, minimum-order fee, documentation fee, fuel surcharge, or other amount not expressly stated in the applicable Order without Buyer’s prior written approval.",
      },
    ],
  },
  {
    title: "3. Delivery",
    blocks: [
      {
        p: "Unless otherwise expressly stated in the applicable Order, all goods shall be delivered Delivery Duty Paid (“DDP”) to the delivery location specified by Buyer, in accordance with the latest version of Incoterms published by the International Chamber of Commerce.",
      },
      {
        p: "Risk of loss and title to the goods, free and clear of all liens, claims, security interests, and other encumbrances, shall pass to Buyer upon completed delivery at the location specified in the Order, subject to Buyer’s inspection and rejection rights.",
      },
      {
        p: "Supplier shall not deliver goods before the agreed delivery date without Buyer’s prior written approval. Buyer shall not be liable for costs associated with premature production, storage, installation, assembly, commissioning, or delivery unless Buyer expressly agrees to those costs in writing.",
      },
      {
        p: "Where the Agreement requires installation, commissioning, testing, inspection, processing, cutting, machining, packaging, or other work by Supplier, such work shall be performed professionally, in accordance with good industry practices, using qualified personnel and suitable materials and equipment.",
      },
      { p: "Supplier shall:" },
      {
        ol: [
          "Properly package, preserve, identify, mark, and ship the goods to prevent contamination, corrosion, deterioration, substitution, commingling, or damage during transportation, unloading, handling, and storage;",
          "Follow all packaging, labeling, shipping, and routing instructions stated in the Order;",
          "Identify the applicable Order number on all packages, packing slips, bills of lading, invoices, certificates, and correspondence;",
          "Maintain heat, lot, batch, serial-number, or other traceability required by the Agreement;",
          "Provide all Documentation required by the Agreement at or before delivery;",
          "Ensure that Documentation accurately corresponds to the goods delivered;",
          "Maintain separation and identification of goods where different heats, lots, specifications, grades, origins, or conditions are included in the same shipment; and",
          "Comply with all applicable transportation and hazardous-material requirements.",
        ],
      },
      {
        p: "Unless expressly permitted in the Order, Supplier shall not make substitutions involving material grade, alloy, temper, condition, specification, standard, manufacturer, producing mill, country of origin, country of melt, dimensions, tolerances, quantity, condition, or certification.",
      },
      {
        p: "If the goods include software, firmware, device drivers, or other software required for the operation, support, testing, resale, or distribution of the goods (“Software”), Supplier grants Buyer and its Affiliates a perpetual, non-exclusive, irrevocable, royalty-free, worldwide license to use, reproduce, install, execute, maintain, and distribute the Software solely in connection with Buyer’s use, resale, distribution, support, or servicing of the goods.",
      },
      {
        p: "Unless otherwise stated in the Agreement, all Software license fees shall be included in the purchase price.",
      },
    ],
  },
  {
    title: "4. Warranties",
    blocks: [
      {
        p: "Without limiting any express or implied warranty available under applicable law, Supplier represents and warrants that all goods supplied under the Agreement:",
      },
      {
        ol: [
          "Conform strictly to the Agreement, including all descriptions, specifications, drawings, dimensions, tolerances, quantities, weights, approved samples, standards, certifications, and Documentation;",
          "Are merchantable and suitable for their intended and represented purposes;",
          "Are free from defects in design, material, manufacture, processing, workmanship, title, and Documentation;",
          "Are authentic and have not been counterfeited, altered, misrepresented, substituted, or improperly relabeled;",
          "Are free and clear of all liens, claims, security interests, and other encumbrances;",
          "Have been properly stored, handled, preserved, packaged, and transported;",
          "Have not been exposed to conditions that could adversely affect their quality, integrity, mechanical properties, chemical composition, performance, or useful life;",
          "Comply with all applicable industry standards and legal requirements;",
          "Match the condition represented by Supplier in its quotation, listing, inventory record, or other communication;",
          "Are accompanied by complete, accurate, authentic, and unaltered Documentation;",
          "Have the represented origin, melt source, manufacturing source, and traceability; and",
          "Do not infringe, misappropriate, or otherwise violate the intellectual property or proprietary rights of any third party.",
        ],
      },
      {
        p: "Unless the Agreement expressly identifies the goods as used, surplus, excess, aged, remnant, off-cut, secondary, customer return, or another non-new condition, Supplier warrants that the goods are new and unused.",
      },
      {
        p: "Where goods are expressly identified as surplus, excess, remnant, aged, secondary, or otherwise non-new, Supplier warrants that:",
      },
      {
        ol: [
          "The stated condition is complete and accurate;",
          "All known damage, corrosion, deterioration, modifications, prior processing, missing Documentation, storage conditions, or other material limitations have been fully disclosed;",
          "The goods remain suitable for the purpose represented by Supplier; and",
          "No material fact affecting the value, compliance, usability, or resale of the goods has been omitted.",
        ],
      },
      {
        p: "Supplier further warrants that any material test report, mill test report, certificate of conformance, inspection report, laboratory report, country-of-origin statement, DFARS representation, Buy American representation, domestic-material representation, export classification, or other certification supplied by Supplier is authentic, accurate, complete, and applicable to the specific goods delivered.",
      },
      {
        p: "Supplier shall not provide Documentation belonging to a different heat, lot, batch, size, product form, producer, manufacturing location, specification, or shipment.",
      },
      {
        p: "Buyer’s approval of a sample, drawing, quotation, listing, or Documentation shall not relieve Supplier of its warranty obligations.",
      },
      {
        p: "If goods do not comply with this Section, such goods shall be considered defective or nonconforming. Buyer may, at its discretion and without prejudice to any other right or remedy:",
      },
      {
        ol: [
          "Reject the defective or nonconforming goods;",
          "Require Supplier to promptly repair, rework, recertify, or replace the goods;",
          "Require a full or partial refund;",
          "Obtain replacement goods from another source and charge Supplier for any resulting additional costs;",
          "Require Supplier to retrieve the rejected goods at Supplier’s expense;",
          "Correct or rework the goods and charge Supplier for the associated costs; or",
          "Exercise any other remedy available under the Agreement or applicable law.",
        ],
      },
      {
        p: "Supplier shall be responsible for all costs and expenses resulting from defective or nonconforming goods, including inspection, testing, analysis, sorting, segregation, recall, handling, storage, rework, replacement, transportation, expedited freight, disposal, customer claims, administrative expenses, and reasonable attorneys’ fees.",
      },
      {
        p: "Risk of loss for rejected goods shall pass back to Supplier upon Buyer’s notice of rejection.",
      },
      {
        p: "Where applicable to the goods or services, Supplier shall provide commercially reasonable post-warranty support for five (5) years following the last date of manufacture or delivery.",
      },
    ],
  },
  {
    title: "5. Epidemic or Systemic Defects",
    blocks: [
      {
        p: "Supplier warrants that the goods will be free from epidemic or systemic defects.",
      },
      {
        p: "An “Epidemic Defect” means a defect, nonconformity, false certification, traceability failure, or other substantially similar problem that:",
      },
      {
        ol: [
          "Appears in more than one-half of one percent (0.5%), or any lower percentage specified in the Agreement, of goods of the same or substantially similar type supplied by Supplier;",
          "Affects multiple units, pieces, lots, heats, batches, shipments, or Orders; or",
          "Has the same or a substantially similar cause, regardless of the percentage affected.",
        ],
      },
      {
        p: "If an Epidemic Defect occurs, Supplier shall immediately investigate the cause, notify Buyer, identify all potentially affected goods, and provide a corrective-action and containment plan.",
      },
      {
        p: "At Supplier’s sole cost, Supplier shall promptly repair, replace, recertify, retest, or refund affected goods and shall reimburse and indemnify Buyer for all resulting losses and expenses, including inspection, testing, sorting, recall, customer notification, transportation, replacement, disposal, and interruption costs.",
      },
    ],
  },
  {
    title: "6. Payment",
    blocks: [
      {
        p: "Supplier shall submit complete and accurate invoices through the Platform or by another method designated by Buyer.",
      },
      { p: "Each invoice must include, as applicable:" },
      {
        ol: [
          "Supplier’s legal name and remittance information;",
          "The applicable Order number;",
          "The goods or services provided;",
          "Quantities, unit prices, and total price;",
          "Shipment and delivery information;",
          "Applicable taxes;",
          "Supporting Documentation; and",
          "Any other information required by the Order or Platform.",
        ],
      },
      {
        p: "Payment is subject to Buyer’s receipt and acceptance of the goods and all required Documentation.",
      },
      {
        p: "Payment shall be made according to the payment terms stated in the applicable Order. If no payment term is stated, payment shall be due sixty (60) days after the later of:",
      },
      {
        ol: [
          "Buyer’s receipt and acceptance of the goods;",
          "Completion and acceptance of applicable installation, testing, or services;",
          "Buyer’s receipt of all required Documentation; or",
          "Buyer’s receipt of a complete and accurate invoice.",
        ],
      },
      {
        p: "Buyer may withhold payment of disputed amounts without being considered in default. Buyer’s payment shall not constitute acceptance of the goods or a waiver of any right, warranty, claim, or remedy.",
      },
      {
        p: "Supplier shall accept payment regardless of whether payment is made by Buyer, an Affiliate of Buyer, a payment processor, financing provider, customer, or other third party designated by Buyer.",
      },
      {
        p: "Buyer shall have the right to set off any amount owed by Supplier or its Affiliates to Buyer or its Affiliates against any amount owed by Buyer or its Affiliates to Supplier or its Affiliates.",
      },
      {
        p: "Any debt owed by Buyer to Supplier may be paid on Buyer’s behalf by an Affiliate or designated third party. Supplier shall treat such payment as if made directly by Buyer, and Buyer’s debt shall be satisfied to the extent of the payment.",
      },
      {
        p: "Buyer may deduct from amounts otherwise payable any credits, refunds, chargebacks, claims, penalties, platform fees, commissions, transaction fees, freight adjustments, or other amounts that Supplier has expressly agreed to pay or that are properly chargeable to Supplier under the Agreement.",
      },
    ],
  },
  {
    title: "7. Prices",
    blocks: [
      {
        p: "All prices shall be firm and shall include all costs necessary to supply the goods in full compliance with the Agreement, including packaging, preservation, labeling, Documentation, testing, inspection, handling, and delivery, except for amounts expressly identified separately in the Order.",
      },
      {
        p: "Prices shall include all applicable taxes, duties, tariffs, fees, and assessments except to the extent the applicable Order or Incoterm expressly provides that such amounts are Buyer’s responsibility.",
      },
      {
        p: "Supplier shall pay all taxes, duties, tariffs, fees, and assessments for which Supplier is responsible. If Buyer pays an amount that Supplier was obligated to pay, Supplier shall promptly reimburse Buyer.",
      },
      {
        p: "Supplier warrants that the prices offered to Buyer are not less favorable than prices offered by Supplier to other customers purchasing substantially comparable goods in similar quantities and under comparable commercial conditions.",
      },
      {
        p: "Supplier shall not change an accepted price without Buyer’s prior written approval.",
      },
      {
        p: "Supplier is solely responsible for ensuring the accuracy of all prices, quantities, weights, availability, lead times, specifications, and other information submitted through the Platform or included in a quotation.",
      },
    ],
  },
  {
    title: "8. Inspection",
    blocks: [
      {
        p: "Buyer may inspect, test, measure, analyze, audit, or otherwise evaluate the goods, Supplier’s facilities, Supplier’s quality systems, and relevant records at any reasonable time.",
      },
      {
        p: "Buyer may request information concerning the current location, condition, production status, testing status, and shipment status of any Order. Supplier shall provide such information promptly.",
      },
      {
        p: "Buyer may perform or obtain chemical analysis, positive material identification, mechanical testing, dimensional inspection, nondestructive testing, destructive testing, documentation verification, or other testing reasonably necessary to confirm compliance.",
      },
      {
        p: "Payment, inspection, testing, receipt, resale, or acceptance of goods shall not:",
      },
      {
        ol: [
          "Relieve Supplier of its obligations;",
          "Constitute approval of the goods;",
          "Waive any defect, latent defect, nonconformity, certification issue, or documentation issue; or",
          "Waive any right or remedy available to Buyer.",
        ],
      },
      {
        p: "Supplier shall maintain records sufficient to establish compliance with the Agreement for at least seven (7) years after the applicable delivery, or for any longer period required by law, the Order, an applicable specification, or Buyer’s customer requirements.",
      },
    ],
  },
  {
    title: "9. Drawings, Tools, Data, and Materials",
    blocks: [
      {
        p: "Any specifications, drawings, designs, technical information, samples, models, tools, molds, dies, jigs, fixtures, masks, equipment, software, data, Documentation, or other materials furnished, funded, or paid for by Buyer, in whole or in part (“Buyer Materials”), shall remain or become Buyer’s property.",
      },
      { p: "Supplier shall:" },
      {
        ol: [
          "Use Buyer Materials exclusively to perform the Agreement;",
          "Properly maintain and protect Buyer Materials at Supplier’s expense;",
          "Clearly identify Buyer Materials as Buyer’s property;",
          "Keep Buyer Materials separate from other property when not in use;",
          "Not copy, disclose, transfer, sell, pledge, encumber, or use Buyer Materials for any other customer or purpose;",
          "Promptly notify Buyer of any loss, damage, claim, attachment, or attempted seizure involving Buyer Materials; and",
          "Promptly return or deliver Buyer Materials at Buyer’s request.",
        ],
      },
      {
        p: "Risk of loss or damage to Buyer Materials shall remain with Supplier while such materials are in Supplier’s possession or control.",
      },
      {
        p: "Supplier shall maintain adequate insurance covering Buyer Materials and, where requested, identify Buyer as a loss payee or beneficiary.",
      },
    ],
  },
  {
    title: "10. Indemnification",
    blocks: [
      {
        p: "Without limiting any other right or remedy, Supplier shall defend, indemnify, and hold harmless Buyer, its Affiliates, customers, successors, assigns, officers, directors, employees, agents, contractors, and representatives from and against all claims, demands, actions, liabilities, losses, damages, penalties, fines, judgments, settlements, costs, and expenses, including reasonable attorneys’ fees, arising from or relating to:",
      },
      {
        ol: [
          "Supplier’s breach of the Agreement;",
          "Defective, nonconforming, counterfeit, misrepresented, or improperly certified goods;",
          "Supplier’s negligence, gross negligence, fraud, willful misconduct, or violation of law;",
          "Personal injury, death, or property damage caused by Supplier or the goods;",
          "Any allegation that the goods or services infringe, misappropriate, or violate a third party’s intellectual property or proprietary rights;",
          "Inaccurate, altered, fraudulent, or inapplicable Documentation;",
          "Supplier’s failure to comply with export-control, sanctions, environmental, labor, safety, or other applicable laws;",
          "Supplier’s failure to pay taxes, duties, subcontractors, employees, or other amounts for which Supplier is responsible; or",
          "Any act or omission of Supplier’s officers, employees, agents, contractors, or subcontractors.",
        ],
      },
      {
        p: "If a claim alleges that goods or services infringe a third party’s intellectual property rights, Supplier shall, at Buyer’s option and at no additional cost to Buyer:",
      },
      {
        ol: [
          "Procure the right for Buyer and its customers to continue using, reselling, or distributing the goods;",
          "Modify or replace the goods so that they are noninfringing without reducing their functionality, quality, or value; or",
          "Refund all amounts paid and reimburse Buyer for all resulting costs and losses.",
        ],
      },
      {
        p: "Supplier’s indemnification obligations shall survive delivery, inspection, payment, acceptance, expiration, and termination of the Agreement.",
      },
    ],
  },
  {
    title: "11. Confidential Information and Platform Data",
    blocks: [
      {
        p: "Supplier shall treat as confidential and use solely for the performance of the Agreement all nonpublic information provided by or on behalf of Buyer, its Affiliates, the Platform, or Buyer’s customers, whether provided orally, electronically, visually, or in writing (“Confidential Information”).",
      },
      { p: "Confidential Information includes:" },
      {
        ol: [
          "Technical, commercial, financial, and operational information;",
          "Customer and prospective-customer identities;",
          "Supplier identities and supply sources;",
          "RFQs, purchase requirements, specifications, quantities, forecasts, pricing, and transaction information;",
          "Inventory data, availability, lead times, and sourcing information;",
          "Platform designs, features, algorithms, models, software, analytics, reports, and processes;",
          "Business plans, strategies, relationships, and opportunities;",
          "Credentials, access information, and security information; and",
          "The existence and terms of an Agreement.",
        ],
      },
      { p: "Supplier shall:" },
      {
        ol: [
          "Provide access to Confidential Information only to personnel who have a legitimate need to know and who are bound by confidentiality obligations;",
          "Protect Confidential Information using at least reasonable care;",
          "Not disclose, sell, publish, transfer, scrape, download, aggregate, analyze, reverse engineer, or otherwise use Confidential Information except to perform the Agreement;",
          "Not use customer, RFQ, pricing, transaction, or contact information to solicit or transact outside the authorized Kono.ai process without Buyer’s prior written approval;",
          "Promptly notify Buyer of any unauthorized access, use, disclosure, loss, or security incident; and",
          "Return or destroy Confidential Information upon Buyer’s request, except for copies required to be retained by law.",
        ],
      },
      {
        p: "All Confidential Information shall remain the property of Buyer or the applicable owner. No license or other right is granted except the limited right to use Confidential Information to perform the Agreement.",
      },
      {
        p: "Supplier shall not use the name, logo, trademark, customer information, transaction information, or any other reference to Buyer, Kono.ai, Buyer’s Affiliates, or Buyer’s customers in advertising, press releases, customer lists, sales literature, case studies, social media, or other publications without Buyer’s prior written approval.",
      },
      {
        p: "Supplier’s confidentiality obligations shall survive expiration or termination of the Agreement.",
      },
    ],
  },
  {
    title: "12. Termination",
    blocks: [
      {
        p: "Buyer may terminate all or any part of an Agreement for convenience upon written notice to Supplier.",
      },
      {
        p: "If Buyer provides notice at least thirty (30) days before the scheduled shipment date, Buyer shall have no liability for the termination except for conforming goods already delivered and accepted.",
      },
      {
        p: "If Buyer terminates on less than thirty (30) days’ notice, Buyer and Supplier shall negotiate a reasonable termination charge, if any, based on documented and unavoidable costs directly incurred by Supplier before termination.",
      },
      {
        p: "In determining any termination charge, the parties shall consider:",
      },
      {
        ol: [
          "The percentage of work properly completed;",
          "Supplier’s ability to stop, reuse, return, resell, or redirect the goods or materials;",
          "The market value of the goods;",
          "Costs avoided by Supplier;",
          "Payments previously made by Buyer; and",
          "Supplier’s duty to mitigate losses.",
        ],
      },
      {
        p: "Supplier shall not be entitled to anticipated profit, lost opportunity, overhead allocation, consequential damages, or charges not supported by verifiable records.",
      },
      {
        p: "Buyer may audit all elements of a proposed termination charge. Supplier shall provide all relevant books, records, invoices, time records, inventory records, and other supporting information.",
      },
      {
        p: "Supplier shall be in default if Supplier fails to comply with any obligation under the Agreement.",
      },
      {
        p: "Buyer may immediately suspend or terminate all or part of an Agreement if:",
      },
      {
        ol: [
          "Supplier breaches the Agreement;",
          "Supplier fails to provide adequate assurances of performance;",
          "Supplier provides false or misleading information or Documentation;",
          "Supplier becomes insolvent or subject to bankruptcy, receivership, liquidation, or similar proceedings;",
          "Supplier makes an assignment for the benefit of creditors;",
          "Supplier ceases or threatens to cease operations;",
          "An attachment or similar action is made against Supplier’s assets;",
          "Supplier experiences a material change of control;",
          "Supplier violates applicable law;",
          "Supplier creates a material cybersecurity, confidentiality, reputational, compliance, or supply-chain risk; or",
          "Buyer reasonably believes Supplier will be unable to perform.",
        ],
      },
      {
        p: "Termination shall be without prejudice to any other right or remedy available to Buyer.",
      },
      {
        p: "Provisions that by their nature are intended to survive expiration or termination shall survive, including warranties, confidentiality, intellectual property, indemnification, audit, payment adjustment, governing law, and dispute-resolution provisions.",
      },
    ],
  },
  {
    title: "13. Continuity of Supply",
    blocks: [
      {
        p: "If Supplier defaults under an Agreement and fails to cure the default within fourteen (14) days after notice, or fails within that period to take precautions reasonably satisfactory to Buyer to prevent similar future defaults, Buyer may obtain the goods or services directly from Supplier’s manufacturer, subcontractor, or another third party.",
      },
      {
        p: "Supplier authorizes Buyer, upon notice of an actual or anticipated material default, to contact Supplier’s applicable manufacturers and subcontractors to prepare for continuity of supply.",
      },
      {
        p: "If Supplier does not use a subcontractor, or if the subcontractor is unable or unwilling to supply Buyer, Supplier shall provide Buyer with the materials, information, specifications, Documentation, tooling, data, and other items reasonably necessary to enable Buyer or its designee to manufacture, source, support, distribute, license, or sell the goods or perform the services (“Continuity Materials”).",
      },
      {
        p: "To the extent necessary to maintain continuity of supply, Supplier grants Buyer a worldwide, royalty-free, irrevocable, non-exclusive license under Supplier’s applicable intellectual property rights to:",
      },
      {
        ol: [
          "Use, reproduce, execute, modify, and prepare derivative works of the Continuity Materials;",
          "Manufacture, source, support, distribute, and sell the affected goods;",
          "Perform the affected services; and",
          "Authorize third parties to perform the foregoing activities on Buyer’s behalf.",
        ],
      },
      {
        p: "Buyer shall require any third party receiving Supplier’s confidential Continuity Materials to use them solely for continuity-of-supply purposes and to protect them under reasonable confidentiality obligations.",
      },
      {
        p: "Supplier’s warranty and indemnification obligations shall apply to goods manufactured or services performed under this Section to the extent the relevant defect or claim arises from Supplier’s designs, specifications, Documentation, materials, instructions, intellectual property, or prior acts or omissions.",
      },
    ],
  },
  {
    title: "14. Compliance with Law",
    blocks: [
      {
        p: "Supplier represents and warrants that the goods have been and will be designed, manufactured, processed, stored, handled, packaged, labeled, transported, exported, imported, and delivered, and that services have been and will be performed, in compliance with all applicable laws, regulations, rules, standards, and governmental requirements.",
      },
      {
        p: "These obligations include, where applicable, laws and requirements relating to:",
      },
      {
        ol: [
          "Environmental protection;",
          "Occupational health and safety;",
          "Hazardous materials;",
          "Employment, wages, working conditions, forced labor, child labor, and human trafficking;",
          "Anti-bribery and anti-corruption;",
          "Import, export, customs, and economic sanctions;",
          "Product safety and labeling;",
          "Data protection, privacy, and cybersecurity;",
          "Conflict minerals and responsible sourcing;",
          "Country of origin and marking;",
          "Government procurement;",
          "Domestic-content and specialty-metals requirements; and",
          "Buyer’s compliance policies communicated to Supplier.",
        ],
      },
      {
        p: "Where required by the Order, Supplier shall comply with applicable specifications and requirements, including ASTM, AMS, ASME, SAE, AWS, ISO, DFARS, FAR, ITAR, EAR, RoHS, REACH, and other standards or regulatory requirements identified by Buyer.",
      },
      {
        p: "Supplier shall not represent goods as “domestic,” “DFARS compliant,” “Buy American compliant,” “melted in the United States,” “manufactured in the United States,” or similar unless the representation is accurate, supported by Documentation, and applicable to the particular goods delivered.",
      },
      {
        p: "For dangerous or hazardous goods, Supplier shall provide complete safety data sheets, composition information, handling instructions, transportation classifications, and all other information necessary for safe and lawful transportation, storage, processing, and use.",
      },
      {
        p: "Supplier shall promptly notify Buyer of any actual or suspected violation of law, regulatory inquiry, sanction, debarment, exclusion, product recall, certification withdrawal, or other event that may affect Supplier’s performance or the goods.",
      },
    ],
  },
  {
    title: "15. Use of Trademarks and Trade Names",
    blocks: [
      {
        p: "Supplier may use any trademark, trade name, logo, domain name, product name, or other designation owned by Buyer, Kono.ai, Buyer’s Affiliates, or Buyer’s customers only in accordance with Buyer’s prior written instructions and solely for the purpose expressly authorized by Buyer.",
      },
      {
        p: "Supplier shall not acquire any right, title, license, or interest in Buyer’s trademarks, trade names, logos, domain names, platform branding, or other proprietary designations.",
      },
      {
        p: "Any goodwill resulting from Supplier’s authorized use of Buyer’s trademarks or trade names shall inure exclusively to Buyer’s benefit.",
      },
      {
        p: "Supplier shall not register or attempt to register any mark, name, domain, social-media identifier, or designation that is identical or confusingly similar to Kono.ai or any designation owned by Buyer or its Affiliates.",
      },
    ],
  },
  {
    title: "16. Subcontracting and Assignment",
    blocks: [
      {
        p: "Supplier shall not subcontract, delegate, transfer, or assign any right or obligation under the Agreement without Buyer’s prior written approval.",
      },
      {
        p: "If Buyer permits Supplier to use a subcontractor, Supplier shall remain fully responsible and liable for the subcontractor’s acts, omissions, compliance, quality, delivery, Documentation, confidentiality, and performance.",
      },
      {
        p: "Supplier shall ensure that all approved subcontractors are bound by written obligations consistent with the Agreement.",
      },
      {
        p: "Buyer may assign or transfer an Agreement to an Affiliate or to a third party in connection with a merger, acquisition, financing, restructuring, reorganization, change of control, sale of assets, or other transaction involving all or part of Buyer’s business or assets.",
      },
    ],
  },
  {
    title: "17. No Waiver",
    blocks: [
      {
        p: "No waiver by Buyer of any breach, condition, covenant, or term of the Agreement shall be effective unless made in writing by an authorized representative of Buyer.",
      },
      {
        p: "Buyer’s failure or delay in enforcing any provision or exercising any right, power, or privilege shall not operate as a waiver.",
      },
      {
        p: "A waiver relating to one event shall not constitute a waiver relating to any other event.",
      },
    ],
  },
  {
    title: "18. Export Restrictions and Sanctions",
    blocks: [
      {
        p: "Supplier shall comply with all applicable United States and international export-control, import-control, customs, trade-sanctions, antiboycott, and economic-sanctions laws.",
      },
      {
        p: "Supplier shall not export, re-export, transfer, release, disclose, or provide, directly or indirectly, any goods, software, technology, technical data, Documentation, or Buyer Confidential Information in violation of applicable law.",
      },
      {
        p: "Supplier shall not supply goods from, through, or involving a country, entity, or person subject to applicable sanctions or restrictions without Buyer’s prior written approval and all required government authorizations.",
      },
      {
        p: "Supplier shall inform Buyer, before accepting an Order, whether any goods, Software, technology, Documentation, or technical data are controlled under the laws of the United States or another jurisdiction.",
      },
      { p: "Supplier shall provide, where applicable:" },
      {
        ol: [
          "The Export Control Classification Number;",
          "The applicable United States Munitions List category;",
          "The Harmonized Tariff Schedule classification;",
          "Country-of-origin information;",
          "License requirements;",
          "Sanctions restrictions; and",
          "Any other export, import, or customs classification requested by Buyer.",
        ],
      },
      {
        p: "Supplier shall promptly notify Buyer of any change in an applicable classification, license requirement, restriction, or authorization.",
      },
    ],
  },
  {
    title: "19. Consequential Damages",
    blocks: [
      {
        p: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, SUPPLIER SHALL NOT BE ENTITLED TO RECOVER FROM BUYER OR ITS AFFILIATES ANY LOST PROFITS, LOST REVENUE, LOSS OF BUSINESS, LOSS OF GOODWILL, LOSS OF OPPORTUNITY, PRODUCTION DOWNTIME, OR INDIRECT, SPECIAL, INCIDENTAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES ARISING FROM OR RELATING TO AN AGREEMENT, EVEN IF BUYER HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.",
      },
      {
        p: "This limitation shall not limit Supplier’s indemnification obligations or Buyer’s right to recover damages resulting from Supplier’s breach, fraud, gross negligence, willful misconduct, violation of confidentiality obligations, infringement, false certification, or violation of law.",
      },
    ],
  },
  {
    title: "20. Governing Law and Dispute Resolution",
    blocks: [
      {
        p: "The Agreement and these Terms and Conditions shall be governed by the laws of the State of California, without regard to its conflict-of-laws principles.",
      },
      {
        p: "The United Nations Convention on Contracts for the International Sale of Goods shall not apply.",
      },
      {
        p: "Supplier and Buyer shall first attempt to resolve any dispute arising out of or relating to the Agreement through good-faith consultation and negotiation.",
      },
      {
        p: "If the dispute is not resolved within thirty (30) days after written notice of the dispute, either party may submit the dispute to the state or federal courts located in Los Angeles County, California.",
      },
      {
        p: "Supplier consents to the personal jurisdiction and venue of such courts and waives any objection based on inconvenient forum or lack of personal jurisdiction.",
      },
      {
        p: "Nothing in this Section prevents Buyer from seeking temporary, preliminary, or permanent injunctive relief in any court of competent jurisdiction to protect Confidential Information, intellectual property, Buyer Materials, Platform data, or other proprietary rights.",
      },
    ],
  },
  {
    title: "21. Severability",
    blocks: [
      {
        p: "If any provision of the Agreement or these Terms and Conditions is held to be invalid, illegal, or unenforceable by a court of competent jurisdiction or by legislative or administrative action, the provision shall be enforced to the maximum extent permitted by law.",
      },
      {
        p: "The invalidity or unenforceability of any provision shall not affect the validity or enforceability of any other provision.",
      },
      {
        p: "The parties intend that any invalid or unenforceable provision be modified only to the minimum extent necessary to make it valid and enforceable while preserving its original commercial purpose.",
      },
    ],
  },
];
